/**
 * Guided Setup Wizard - Step-by-step onboarding for new properties
 * 
 * This is a frontend-only overlay that orchestrates existing backend APIs.
 * It guides users through: Locations & Tags → Employees → Notification Recipients → Branding
 * 
 * Does NOT modify or replace any existing backend functionality.
 * The existing portal remains fully accessible underneath.
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Plus,
  Trash2,
  Tag,
  Mail,
  Phone,
  Image,
  Upload,
  X,
  Loader2,
  Sparkles,
  MapPin,
  Bell,
  Palette,
  CheckCircle2,
  AlertCircle,
  DoorOpen,
  Droplets,
  Smartphone,
  UserPlus,
  Users,
  Pencil,
  Wifi,
  Zap,
  Activity,
  MessageSquare,
} from 'lucide-react';

const STEPS = [
  { id: 'welcome', title: 'Welcome', icon: Sparkles },
  { id: 'locations', title: 'Locations & Tags', icon: MapPin },
  { id: 'employees', title: 'Employees', icon: Users },
  { id: 'notifications', title: 'Notifications', icon: Bell },
  { id: 'branding', title: 'Branding', icon: Palette },
  { id: 'complete', title: 'System Status', icon: CheckCircle2 },
];

export const SetupWizard = ({ open, onClose, hotel, orgId, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const token = localStorage.getItem('hotels_token') || localStorage.getItem('token');

  // Locations state
  const [roomLocations, setRoomLocations] = useState([]);
  const [restroomLocations, setRestroomLocations] = useState([]);
  const [retailerLocations, setRetailerLocations] = useState([]);
  const [activeLocationType, setActiveLocationType] = useState('room');

  // New location form
  const [newLocationName, setNewLocationName] = useState('');
  const [newLocationFloor, setNewLocationFloor] = useState('');

  // Tag assignment
  const [assigningTagFor, setAssigningTagFor] = useState(null);
  const [tagIdInput, setTagIdInput] = useState('');
  const [tagLabelInput, setTagLabelInput] = useState('');

  // Notification state
  const [recipients, setRecipients] = useState([]);
  const [carrierList, setCarrierList] = useState([]);
  const [newRecipientType, setNewRecipientType] = useState('email');
  const [newRecipientName, setNewRecipientName] = useState('');
  const [newRecipientAddress, setNewRecipientAddress] = useState('');
  const [newRecipientPhone, setNewRecipientPhone] = useState('');
  const [newRecipientCarrier, setNewRecipientCarrier] = useState('');

  // Branding state
  const [logoUrl, setLogoUrl] = useState('');
  const [logoEnabled, setLogoEnabled] = useState(false);
  const [logoSize, setLogoSize] = useState('medium');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoUrlInput, setLogoUrlInput] = useState('');

  // Employees state
  const [employees, setEmployees] = useState([]);
  const [newEmpName, setNewEmpName] = useState('');
  const [newEmpEmail, setNewEmpEmail] = useState('');
  const [newEmpPhone, setNewEmpPhone] = useState('');
  const [newEmpRole, setNewEmpRole] = useState('staff');
  // Engagement types from hotel config
  const surveyEnabled = hotel?.engagement_modes?.in_room_survey?.enabled || false;
  const restroomEnabled = hotel?.engagement_modes?.restroom_survey?.enabled || false;
  const retailerEnabled = hotel?.engagement_modes?.retailer_alert?.enabled || false;

  // Determine which location types are available
  const locationTypes = [];
  if (surveyEnabled) locationTypes.push({ id: 'room', label: 'Room Tags', icon: DoorOpen, color: 'green' });
  if (restroomEnabled) locationTypes.push({ id: 'restroom', label: 'Restroom Tags', icon: Droplets, color: 'blue' });
  if (retailerEnabled) locationTypes.push({ id: 'retailer', label: 'Retailer Alert Tags', icon: AlertCircle, color: 'fuchsia' });
  if (locationTypes.length === 0) locationTypes.push({ id: 'room', label: 'Room Tags', icon: DoorOpen, color: 'green' });

  // Load data on open
  useEffect(() => {
    if (open && orgId) {
      fetchLocations();
      fetchEmployees();
      fetchRecipients();
      fetchCarriers();
      loadBranding();
      // Set default active location type
      if (retailerEnabled) setActiveLocationType('retailer');
      else if (restroomEnabled) setActiveLocationType('restroom');
      else setActiveLocationType('room');
    }
  }, [open, orgId]);

  const authHeaders = { 'Authorization': `Bearer ${token}` };
  const jsonHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  // ===== DATA FETCHING =====
  const fetchLocations = async () => {
    try {
      const [roomRes, restroomRes, retailerRes] = await Promise.all([
        fetch(`${API_BASE}/api/hotels/${orgId}/rooms`, { headers: authHeaders }).then(r => r.ok ? r.json() : []),
        fetch(`${API_BASE}/api/hotels-standalone/organizations/${orgId}/restroom-locations`, { headers: authHeaders }).then(r => r.ok ? r.json() : []),
        fetch(`${API_BASE}/api/hotels-standalone/organizations/${orgId}/retailer-alert-locations`, { headers: authHeaders }).then(r => r.ok ? r.json() : []),
      ]);
      setRoomLocations(roomRes || []);
      setRestroomLocations(restroomRes || []);
      setRetailerLocations(retailerRes || []);
    } catch (e) {
      console.error('Failed to load locations', e);
    }
  };

  const fetchRecipients = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${orgId}/alert-recipients`, { headers: authHeaders });
      if (res.ok) setRecipients(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchCarriers = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/carriers`, { headers: authHeaders });
      if (res.ok) setCarrierList(await res.json());
    } catch (e) { console.error(e); }
  };

  const loadBranding = () => {
    const mainLogo = hotel?.main_logo || {};
    setLogoUrl(mainLogo.url || '');
    setLogoEnabled(mainLogo.enabled || false);
    setLogoSize(mainLogo.size || 'medium');
  };

  // ===== EMPLOYEE OPERATIONS =====
  const fetchEmployees = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${orgId}/employees`, { headers: authHeaders });
      if (res.ok) setEmployees(await res.json() || []);
    } catch (e) { console.error('Failed to load employees', e); }
  };

  const addEmployee = async () => {
    if (!newEmpName.trim()) { toast.error('Name is required'); return; }
    if (!newEmpEmail.trim() || !newEmpEmail.includes('@')) { toast.error('Valid email is required'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${orgId}/employees`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({ name: newEmpName.trim(), email: newEmpEmail.trim(), phone: newEmpPhone.trim(), role: newEmpRole }),
      });
      if (!res.ok) throw new Error('Failed to add employee');
      toast.success('Employee added');
      setNewEmpName('');
      setNewEmpEmail('');
      setNewEmpPhone('');
      setNewEmpRole('staff');
      await fetchEmployees();
    } catch (e) {
      toast.error(e.message || 'Failed to add employee');
    } finally {
      setSaving(false);
    }
  };

  const deleteEmployee = async (empId) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${orgId}/employees/${empId}`, {
        method: 'DELETE', headers: authHeaders,
      });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Employee removed');
      await fetchEmployees();
    } catch (e) {
      toast.error('Failed to remove employee');
    } finally {
      setSaving(false);
    }
  };

  // ===== LOCATION OPERATIONS =====
  const getActiveLocations = () => {
    if (activeLocationType === 'room') return roomLocations;
    if (activeLocationType === 'restroom') return restroomLocations;
    return retailerLocations;
  };

  const addLocation = async () => {
    if (!newLocationName.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      let url, body;
      if (activeLocationType === 'room') {
        url = `${API_BASE}/api/hotels/${orgId}/rooms`;
        body = { identifier: newLocationName.trim(), floor: newLocationFloor.trim(), room_type: 'standard' };
      } else if (activeLocationType === 'restroom') {
        url = `${API_BASE}/api/hotels-standalone/organizations/${orgId}/restroom-locations`;
        body = { name: newLocationName.trim(), floor: newLocationFloor.trim(), building: '' };
      } else {
        url = `${API_BASE}/api/hotels-standalone/organizations/${orgId}/retailer-alert-locations`;
        body = { name: newLocationName.trim(), floor: newLocationFloor.trim(), building: '' };
      }
      const res = await fetch(url, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Failed to create');
      toast.success('Location added');
      setNewLocationName('');
      setNewLocationFloor('');
      await fetchLocations();
    } catch (e) {
      toast.error(e.message || 'Failed to add location');
    } finally {
      setSaving(false);
    }
  };

  const deleteLocation = async (loc) => {
    setSaving(true);
    try {
      let url;
      if (activeLocationType === 'room') {
        url = `${API_BASE}/api/hotels/${orgId}/rooms/${loc.id}`;
      } else if (activeLocationType === 'restroom') {
        url = `${API_BASE}/api/hotels-standalone/organizations/${orgId}/restroom-locations/${loc.id}`;
      } else {
        url = `${API_BASE}/api/hotels-standalone/organizations/${orgId}/retailer-alert-locations/${loc.id}`;
      }
      const res = await fetch(url, { method: 'DELETE', headers: authHeaders });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Location removed');
      await fetchLocations();
    } catch (e) {
      toast.error('Failed to delete location');
    } finally {
      setSaving(false);
    }
  };

  const assignTag = async (loc) => {
    if (!tagIdInput.trim()) { toast.error('Tag ID is required'); return; }
    setSaving(true);
    try {
      let url, body;
      if (activeLocationType === 'room') {
        url = `${API_BASE}/api/hotels/${orgId}/rooms/${loc.id}/assign-tag`;
        body = { tag_id: tagIdInput.trim(), engagement_type: 'in_room_survey', tag_label: tagLabelInput.trim() || null };
      } else if (activeLocationType === 'restroom') {
        url = `${API_BASE}/api/hotels-standalone/organizations/${orgId}/restroom-locations/${loc.id}/assign-tag`;
        body = { tag_id: tagIdInput.trim(), tag_label: tagLabelInput.trim() || null };
      } else {
        url = `${API_BASE}/api/hotels-standalone/organizations/${orgId}/retailer-alert-locations/${loc.id}/assign-tag`;
        body = { tag_id: tagIdInput.trim(), tag_label: tagLabelInput.trim() || null };
      }
      const res = await fetch(url, { method: activeLocationType === 'room' ? 'POST' : 'PUT', headers: jsonHeaders, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Failed to assign tag');
      toast.success('Tag assigned');
      setAssigningTagFor(null);
      setTagIdInput('');
      setTagLabelInput('');
      await fetchLocations();
    } catch (e) {
      toast.error('Failed to assign tag');
    } finally {
      setSaving(false);
    }
  };

  // ===== RECIPIENT OPERATIONS =====
  const addRecipient = async () => {
    if (newRecipientType === 'email') {
      if (!newRecipientAddress || !newRecipientAddress.includes('@')) {
        toast.error('Valid email address required');
        return;
      }
    } else {
      if (!newRecipientPhone || !newRecipientCarrier) {
        toast.error('Phone number and carrier required');
        return;
      }
    }
    setSaving(true);
    try {
      const body = {
        name: newRecipientName.trim(),
        type: newRecipientType,
        address: newRecipientType === 'email' ? newRecipientAddress.trim() : '',
        phone_number: newRecipientType === 'sms' ? newRecipientPhone.trim() : '',
        carrier: newRecipientType === 'sms' ? newRecipientCarrier : '',
        is_escalation: false,
      };
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${orgId}/alert-recipients`, {
        method: 'POST', headers: jsonHeaders, body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed to add');
      toast.success('Recipient added');
      setNewRecipientName('');
      setNewRecipientAddress('');
      setNewRecipientPhone('');
      setNewRecipientCarrier('');
      await fetchRecipients();
    } catch (e) {
      toast.error('Failed to add recipient');
    } finally {
      setSaving(false);
    }
  };

  const removeRecipient = async (recipientId) => {
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${orgId}/alert-recipients/${recipientId}`, {
        method: 'DELETE', headers: authHeaders,
      });
      if (res.ok) {
        toast.success('Recipient removed');
        await fetchRecipients();
      }
    } catch (e) {
      toast.error('Failed to remove recipient');
    }
  };

  // ===== BRANDING OPERATIONS =====
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) { toast.error('Please upload PNG, JPEG, GIF, WebP, or SVG'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max file size is 5MB'); return; }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_BASE}/api/hotels-standalone/upload/logo`, {
        method: 'POST', headers: authHeaders, body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setLogoUrl(data.url);
      setLogoEnabled(true);
      toast.success('Logo uploaded');
    } catch (e) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoUrlPaste = () => {
    if (logoUrlInput.trim()) {
      setLogoUrl(logoUrlInput.trim());
      setLogoEnabled(true);
      setLogoUrlInput('');
      toast.success('Logo URL set');
    }
  };

  const saveBranding = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${orgId}/main-logo`, {
        method: 'PUT', headers: jsonHeaders,
        body: JSON.stringify({ url: logoUrl || null, size: logoSize, enabled: logoEnabled }),
      });
      if (!res.ok) throw new Error('Failed to save');
      toast.success('Logo saved');
    } catch (e) {
      toast.error('Failed to save logo');
    } finally {
      setSaving(false);
    }
  };

  // ===== NAVIGATION =====
  const nextStep = async () => {
    // Save branding if on branding step before moving to complete
    if (STEPS[currentStep].id === 'branding' && logoUrl) {
      await saveBranding();
    }
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleComplete = () => {
    onComplete?.();
    onClose();
    setCurrentStep(0);
  };

  // ===== SUMMARY DATA =====
  const taggedRooms = roomLocations.filter(r => r.tag_id).length;
  const taggedRestrooms = restroomLocations.filter(r => r.tag_id).length;
  const taggedRetailer = retailerLocations.filter(r => r.tag_id).length;
  const totalTagged = taggedRooms + taggedRestrooms + taggedRetailer;
  const totalLocations = roomLocations.length + restroomLocations.length + retailerLocations.length;

  // ===== STEP RENDERERS =====
  const renderWelcome = () => (
    <div className="flex flex-col items-center text-center py-6 px-4 space-y-6" data-testid="wizard-welcome">
      <div className="w-16 h-16 rounded-2xl bg-blue-500/20 flex items-center justify-center">
        <Sparkles className="w-8 h-8 text-blue-400" />
      </div>
      <div className="space-y-2 max-w-md">
        <h2 className="text-2xl font-semibold text-white tracking-tight">Set up {hotel?.name || 'your property'}</h2>
        <p className="text-sm text-[hsl(215,20%,60%)] leading-relaxed">
          This wizard will walk you through the essentials to get your property operational. 
          You can always adjust these settings later from the main portal.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-3 w-full max-w-sm">
        {[
          { icon: MapPin, label: 'Create locations & assign NFC tags', color: 'text-green-400', bg: 'bg-green-500/10' },
          { icon: Users, label: 'Add your team members', color: 'text-indigo-400', bg: 'bg-indigo-500/10' },
          { icon: Bell, label: 'Set up notification recipients', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
          { icon: Palette, label: 'Configure your branding & logo', color: 'text-purple-400', bg: 'bg-purple-500/10' },
        ].map(({ icon: Icon, label, color, bg }, i) => (
          <div key={i} className={`flex items-center space-x-3 p-3 rounded-lg ${bg} border border-[hsl(217,19%,22%)]`}>
            <Icon className={`w-5 h-5 ${color} flex-shrink-0`} />
            <p className="text-sm text-[hsl(215,20%,75%)] text-left">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );

  const renderLocations = () => {
    const locations = getActiveLocations();
    const activeConfig = locationTypes.find(t => t.id === activeLocationType) || locationTypes[0];
    const colorMap = { green: 'emerald', blue: 'blue', fuchsia: 'fuchsia' };
    const c = colorMap[activeConfig.color] || 'blue';

    return (
      <div className="space-y-4 py-2" data-testid="wizard-locations">
        <div className="text-center space-y-1">
          <h2 className="text-lg font-semibold text-white">Locations & Tags</h2>
          <p className="text-xs text-[hsl(215,20%,55%)]">Create locations, then assign NFC tags to each</p>
        </div>

        {/* Type Selector */}
        {locationTypes.length > 1 && (
          <div className="flex gap-2 justify-center">
            {locationTypes.map(({ id, label, icon: Icon, color }) => (
              <button
                key={id}
                onClick={() => { setActiveLocationType(id); setAssigningTagFor(null); }}
                data-testid={`wizard-loc-type-${id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  activeLocationType === id
                    ? `bg-${color}-500/20 text-${color}-400 ring-1 ring-${color}-500/40`
                    : 'bg-[hsl(217,19%,15%)] text-[hsl(215,20%,55%)] hover:text-white'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Add Location Form */}
        <div className={`p-3 rounded-lg border border-${c}-500/20 bg-${c}-500/5 space-y-2`}>
          <p className="text-xs font-medium text-[hsl(215,20%,70%)]">
            {activeLocationType === 'room' ? 'Add a Room' : 'Add a Location'}
          </p>
          <div className="flex gap-2">
            <Input
              placeholder={activeLocationType === 'room' ? 'Room number (e.g., 101)' : 'Location name (e.g., Main Entrance)'}
              value={newLocationName}
              onChange={(e) => setNewLocationName(e.target.value)}
              className="flex-1 h-9 text-sm bg-[hsl(217,19%,12%)] border-[hsl(217,19%,25%)]"
              data-testid="wizard-new-location-name"
            />
            <Input
              placeholder="Floor"
              value={newLocationFloor}
              onChange={(e) => setNewLocationFloor(e.target.value)}
              className="w-20 h-9 text-sm bg-[hsl(217,19%,12%)] border-[hsl(217,19%,25%)]"
              data-testid="wizard-new-location-floor"
            />
            <Button
              onClick={addLocation}
              disabled={saving || !newLocationName.trim()}
              size="sm"
              className={`h-9 bg-${c}-600 hover:bg-${c}-700`}
              data-testid="wizard-add-location-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Locations List */}
        <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
          {locations.length === 0 ? (
            <div className="text-center py-8 text-[hsl(215,20%,45%)]">
              <MapPin className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No locations yet</p>
              <p className="text-xs mt-1">Add your first location above</p>
            </div>
          ) : (
            locations.map((loc) => {
              const name = loc.identifier || loc.name || 'Unnamed';
              const hasTag = !!loc.tag_id;
              return (
                <div
                  key={loc.id}
                  className={`p-3 rounded-lg border transition-all ${
                    hasTag ? `bg-${c}-500/5 border-${c}-500/25` : 'bg-[hsl(217,19%,14%)] border-[hsl(217,19%,22%)]'
                  }`}
                  data-testid={`wizard-location-${loc.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${hasTag ? `bg-${c}-500/20` : 'bg-[hsl(217,19%,22%)]'}`}>
                        {hasTag ? <Check className={`w-3.5 h-3.5 text-${c}-400`} /> : <Tag className="w-3.5 h-3.5 text-[hsl(215,20%,45%)]" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-white font-medium truncate">{name}</p>
                        {hasTag && (
                          <div className="flex items-center gap-1.5">
                            <span className={`text-[10px] font-mono text-${c}-400`}>{loc.tag_id}</span>
                            {loc.tag_label && <span className="text-[10px] text-[hsl(215,20%,50%)]">- {loc.tag_label}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {!hasTag && assigningTagFor !== loc.id && (
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => { setAssigningTagFor(loc.id); setTagIdInput(''); setTagLabelInput(''); }}
                          className="h-7 text-xs text-blue-400 hover:text-blue-300"
                          data-testid={`wizard-assign-tag-${loc.id}`}
                        >
                          <Tag className="w-3 h-3 mr-1" /> Assign Tag
                        </Button>
                      )}
                      <button
                        onClick={() => deleteLocation(loc)}
                        className="p-1.5 rounded hover:bg-red-500/20 text-[hsl(215,20%,45%)] hover:text-red-400 transition-colors"
                        data-testid={`wizard-delete-location-${loc.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Inline Tag Assignment */}
                  {assigningTagFor === loc.id && (
                    <div className="mt-2 pt-2 border-t border-[hsl(217,19%,22%)] space-y-2">
                      <Input
                        placeholder="Tag ID (e.g., STORE-001)"
                        value={tagIdInput}
                        onChange={(e) => setTagIdInput(e.target.value.toUpperCase())}
                        className="h-8 text-xs bg-[hsl(217,19%,10%)] border-[hsl(217,19%,25%)]"
                        data-testid="wizard-tag-id-input"
                        autoFocus
                      />
                      <Input
                        placeholder="Location label (e.g., Aisle 11 Front)"
                        value={tagLabelInput}
                        onChange={(e) => setTagLabelInput(e.target.value)}
                        className="h-8 text-xs bg-[hsl(217,19%,10%)] border-[hsl(217,19%,25%)]"
                        data-testid="wizard-tag-label-input"
                      />
                      <div className="flex gap-1.5">
                        <Button
                          size="sm" onClick={() => assignTag(loc)}
                          disabled={saving || !tagIdInput.trim()}
                          className={`flex-1 h-7 text-xs bg-${c}-600 hover:bg-${c}-700`}
                          data-testid="wizard-save-tag-btn"
                        >
                          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                          Save Tag
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => { setAssigningTagFor(null); setTagIdInput(''); setTagLabelInput(''); }}
                          className="h-7 text-xs"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Summary */}
        {locations.length > 0 && (
          <div className="flex items-center justify-center gap-3 pt-1">
            <Badge variant="outline" className="text-xs bg-[hsl(217,19%,15%)] border-[hsl(217,19%,25%)] text-[hsl(215,20%,65%)]">
              {locations.length} location{locations.length !== 1 ? 's' : ''}
            </Badge>
            <Badge variant="outline" className={`text-xs ${locations.filter(l => l.tag_id).length > 0 ? `bg-${c}-500/10 text-${c}-400 border-${c}-500/25` : 'bg-[hsl(217,19%,15%)] border-[hsl(217,19%,25%)] text-[hsl(215,20%,55%)]'}`}>
              {locations.filter(l => l.tag_id).length} tagged
            </Badge>
          </div>
        )}
      </div>
    );
  };


  const renderEmployees = () => (
    <div className="space-y-4 py-2" data-testid="wizard-employees">
      <div className="text-center space-y-1">
        <h2 className="text-lg font-semibold text-white">Team Members</h2>
        <p className="text-xs text-[hsl(215,20%,55%)]">Add staff who manage this property. You can always add or remove people later.</p>
      </div>

      {/* Add Employee Form */}
      <div className="p-3 rounded-lg border border-indigo-500/20 bg-indigo-500/5 space-y-2">
        <p className="text-xs font-medium text-[hsl(215,20%,70%)]">Add Team Member</p>
        <div className="grid grid-cols-2 gap-2">
          <Input
            placeholder="Name"
            value={newEmpName}
            onChange={(e) => setNewEmpName(e.target.value)}
            className="h-8 text-sm bg-[hsl(217,19%,12%)] border-[hsl(217,19%,25%)]"
            data-testid="wizard-emp-name"
          />
          <Input
            placeholder="Email"
            type="email"
            value={newEmpEmail}
            onChange={(e) => setNewEmpEmail(e.target.value)}
            className="h-8 text-sm bg-[hsl(217,19%,12%)] border-[hsl(217,19%,25%)]"
            data-testid="wizard-emp-email"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input
            placeholder="Phone (optional)"
            value={newEmpPhone}
            onChange={(e) => setNewEmpPhone(e.target.value)}
            className="h-8 text-sm bg-[hsl(217,19%,12%)] border-[hsl(217,19%,25%)]"
            data-testid="wizard-emp-phone"
          />
          <Select value={newEmpRole} onValueChange={setNewEmpRole}>
            <SelectTrigger className="h-8 text-sm bg-[hsl(217,19%,12%)] border-[hsl(217,19%,25%)]" data-testid="wizard-emp-role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="staff">Staff</SelectItem>
              <SelectItem value="manager">Manager</SelectItem>
              <SelectItem value="supervisor">Supervisor</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={addEmployee}
          disabled={saving || !newEmpName.trim() || !newEmpEmail.trim()}
          size="sm"
          className="w-full h-8 bg-indigo-600 hover:bg-indigo-700"
          data-testid="wizard-add-employee-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <UserPlus className="w-4 h-4 mr-1" />}
          Add Employee
        </Button>
      </div>

      {/* Employees List */}
      <div className="space-y-2 max-h-[260px] overflow-y-auto">
        {employees.length === 0 ? (
          <div className="text-center py-6 text-[hsl(215,20%,45%)]">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No team members yet</p>
            <p className="text-xs mt-1">Add people who work at this property</p>
          </div>
        ) : (
          employees.map(emp => (
            <div key={emp.id} className="flex items-center justify-between p-2.5 bg-[hsl(217,19%,14%)] rounded-lg group" data-testid={`wizard-employee-${emp.id}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-indigo-400 font-medium text-xs">{emp.name?.charAt(0).toUpperCase() || 'E'}</span>
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm text-white font-medium truncate">{emp.name}</p>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-400 font-medium">
                      {(emp.role || 'staff').toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[hsl(215,20%,45%)]">
                    <span className="flex items-center gap-1 truncate">
                      <Mail className="w-3 h-3" />{emp.email}
                    </span>
                    {emp.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />{emp.phone}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => deleteEmployee(emp.id)}
                className="p-1.5 rounded opacity-0 group-hover:opacity-100 hover:bg-red-500/20 text-[hsl(215,20%,45%)] hover:text-red-400 transition-all"
                data-testid={`wizard-delete-employee-${emp.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))
        )}
      </div>

      {employees.length > 0 && (
        <div className="flex items-center justify-center">
          <Badge variant="outline" className="text-xs bg-indigo-500/10 text-indigo-400 border-indigo-500/25">
            {employees.length} team member{employees.length !== 1 ? 's' : ''}
          </Badge>
        </div>
      )}
    </div>
  );


  const renderNotifications = () => (
    <div className="space-y-4 py-2" data-testid="wizard-notifications">
      <div className="text-center space-y-1">
        <h2 className="text-lg font-semibold text-white">Notification Recipients</h2>
        <p className="text-xs text-[hsl(215,20%,55%)]">Who should receive alerts when something needs attention?</p>
      </div>

      {/* Add Recipient Form */}
      <div className="p-3 rounded-lg border border-emerald-500/20 bg-emerald-500/5 space-y-3">
        <div className="flex items-center gap-2">
          <p className="text-xs font-medium text-[hsl(215,20%,70%)]">Add Recipient</p>
          <div className="flex gap-1 ml-auto">
            {['email', 'sms'].map(type => (
              <button
                key={type}
                onClick={() => setNewRecipientType(type)}
                data-testid={`wizard-recipient-type-${type}`}
                className={`text-[10px] px-2.5 py-1 rounded-full font-medium transition-all ${
                  newRecipientType === type
                    ? type === 'email' ? 'bg-blue-500/20 text-blue-400 ring-1 ring-blue-500/30' : 'bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/30'
                    : 'bg-[hsl(217,19%,18%)] text-[hsl(215,20%,50%)]'
                }`}
              >
                {type.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <Input
          placeholder="Name (optional)"
          value={newRecipientName}
          onChange={(e) => setNewRecipientName(e.target.value)}
          className="h-8 text-sm bg-[hsl(217,19%,12%)] border-[hsl(217,19%,25%)]"
          data-testid="wizard-recipient-name"
        />

        {newRecipientType === 'email' ? (
          <Input
            placeholder="Email address"
            type="email"
            value={newRecipientAddress}
            onChange={(e) => setNewRecipientAddress(e.target.value)}
            className="h-8 text-sm bg-[hsl(217,19%,12%)] border-[hsl(217,19%,25%)]"
            data-testid="wizard-recipient-email"
          />
        ) : (
          <div className="space-y-2">
            <Input
              placeholder="Phone number (e.g., +15551234567)"
              value={newRecipientPhone}
              onChange={(e) => setNewRecipientPhone(e.target.value)}
              className="h-8 text-sm bg-[hsl(217,19%,12%)] border-[hsl(217,19%,25%)]"
              data-testid="wizard-recipient-phone"
            />
            <Select value={newRecipientCarrier} onValueChange={setNewRecipientCarrier}>
              <SelectTrigger className="h-8 text-sm bg-[hsl(217,19%,12%)] border-[hsl(217,19%,25%)]" data-testid="wizard-recipient-carrier">
                <SelectValue placeholder="Select carrier" />
              </SelectTrigger>
              <SelectContent>
                {carrierList.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <Button
          onClick={addRecipient}
          disabled={saving}
          size="sm"
          className="w-full h-8 bg-emerald-600 hover:bg-emerald-700"
          data-testid="wizard-add-recipient-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <UserPlus className="w-4 h-4 mr-1" />}
          Add Recipient
        </Button>
      </div>

      {/* Recipients List */}
      <div className="space-y-2 max-h-[240px] overflow-y-auto">
        {recipients.length === 0 ? (
          <div className="text-center py-6 text-[hsl(215,20%,45%)]">
            <Bell className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No recipients configured</p>
            <p className="text-xs mt-1">Add at least one recipient to receive alerts</p>
          </div>
        ) : (
          recipients.map(r => (
            <div key={r.id} className="flex items-center justify-between p-2.5 bg-[hsl(217,19%,14%)] rounded-lg group" data-testid={`wizard-recipient-${r.id}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${r.type === 'sms' ? 'bg-emerald-400' : 'bg-blue-400'}`} />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm text-white truncate">{r.name || r.address || r.phone_number}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${r.type === 'sms' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}`}>
                      {r.type.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-xs text-[hsl(215,20%,45%)] truncate">
                    {r.type === 'sms' ? r.phone_number : r.address}
                  </p>
                </div>
              </div>
              <button
                onClick={() => removeRecipient(r.id)}
                className="p-1.5 rounded opacity-0 group-hover:opacity-100 hover:bg-red-500/20 text-[hsl(215,20%,45%)] hover:text-red-400 transition-all"
                data-testid={`wizard-remove-recipient-${r.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))
        )}
      </div>

      {recipients.length > 0 && (
        <div className="flex items-center justify-center">
          <Badge variant="outline" className="text-xs bg-emerald-500/10 text-emerald-400 border-emerald-500/25">
            {recipients.length} recipient{recipients.length !== 1 ? 's' : ''} configured
          </Badge>
        </div>
      )}
    </div>
  );

  const renderBranding = () => (
    <div className="space-y-4 py-2" data-testid="wizard-branding">
      <div className="text-center space-y-1">
        <h2 className="text-lg font-semibold text-white">Branding & Logo</h2>
        <p className="text-xs text-[hsl(215,20%,55%)]">Your logo appears on surveys and customer-facing pages</p>
      </div>

      {/* Logo Preview */}
      <div className="flex flex-col items-center space-y-3">
        <div className={`w-32 h-32 rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden ${
          logoUrl ? 'border-purple-500/30 bg-white/5' : 'border-[hsl(217,19%,25%)] bg-[hsl(217,19%,14%)]'
        }`}>
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain p-2" />
          ) : (
            <div className="text-center">
              <Image className="w-8 h-8 text-[hsl(215,20%,35%)] mx-auto mb-1" />
              <p className="text-[10px] text-[hsl(215,20%,40%)]">No logo</p>
            </div>
          )}
        </div>

        {logoUrl && (
          <button
            onClick={() => { setLogoUrl(''); setLogoEnabled(false); }}
            className="text-xs text-red-400 hover:text-red-300 transition-colors"
          >
            Remove logo
          </button>
        )}
      </div>

      {/* Upload Options */}
      <div className="space-y-3 max-w-sm mx-auto">
        <div>
          <Label className="text-xs text-[hsl(215,20%,65%)] mb-1.5 block">Upload a file</Label>
          <label className="flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed border-[hsl(217,19%,30%)] bg-[hsl(217,19%,14%)] cursor-pointer hover:border-purple-500/40 hover:bg-[hsl(217,19%,16%)] transition-all"
            data-testid="wizard-logo-upload"
          >
            {uploadingLogo ? (
              <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 text-[hsl(215,20%,55%)]" />
            )}
            <span className="text-sm text-[hsl(215,20%,65%)]">
              {uploadingLogo ? 'Uploading...' : 'Choose image (PNG, JPG, SVG)'}
            </span>
            <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} disabled={uploadingLogo} />
          </label>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex-1 h-px bg-[hsl(217,19%,22%)]" />
          <span className="text-[10px] text-[hsl(215,20%,40%)] uppercase">or</span>
          <div className="flex-1 h-px bg-[hsl(217,19%,22%)]" />
        </div>

        <div>
          <Label className="text-xs text-[hsl(215,20%,65%)] mb-1.5 block">Paste a URL</Label>
          <div className="flex gap-2">
            <Input
              placeholder="https://example.com/logo.png"
              value={logoUrlInput}
              onChange={(e) => setLogoUrlInput(e.target.value)}
              className="h-9 text-sm bg-[hsl(217,19%,12%)] border-[hsl(217,19%,25%)]"
              data-testid="wizard-logo-url-input"
            />
            <Button
              onClick={handleLogoUrlPaste}
              disabled={!logoUrlInput.trim()}
              size="sm"
              className="h-9 bg-purple-600 hover:bg-purple-700"
              data-testid="wizard-logo-url-set-btn"
            >
              Set
            </Button>
          </div>
        </div>

        {logoUrl && (
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2.5 bg-[hsl(217,19%,14%)] rounded-lg">
              <span className="text-sm text-white">Show logo on surveys</span>
              <Switch checked={logoEnabled} onCheckedChange={setLogoEnabled} data-testid="wizard-logo-toggle" />
            </div>
            <div>
              <Label className="text-xs text-[hsl(215,20%,65%)] mb-1.5 block">Logo size</Label>
              <Select value={logoSize} onValueChange={setLogoSize}>
                <SelectTrigger className="h-8 text-sm bg-[hsl(217,19%,12%)] border-[hsl(217,19%,25%)]" data-testid="wizard-logo-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">Small</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="large">Large</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderComplete = () => {
    const hasLocations = totalLocations > 0;
    const hasTags = totalTagged > 0;
    const hasRecipients = recipients.length > 0;

    const configItems = [
      { icon: MapPin, label: 'Locations Configured', value: totalLocations, color: 'text-green-400' },
      { icon: Tag, label: 'Tags Activated', value: totalTagged, color: 'text-blue-400' },
      { icon: Users, label: 'Team Members', value: employees.length, color: 'text-indigo-400' },
      { icon: Bell, label: 'Notification Recipients', value: recipients.length, color: 'text-emerald-400' },
    ];

    const statusItems = [
      { icon: Activity, label: 'Tap Monitoring', status: hasTags ? 'Active' : 'Pending', live: hasTags },
      { icon: Zap, label: 'Alerts', status: (hasTags && hasRecipients) ? 'Enabled' : 'Pending', live: hasTags && hasRecipients },
      { icon: Wifi, label: 'Notifications', status: hasRecipients ? 'Connected' : 'Pending', live: hasRecipients },
      { icon: MessageSquare, label: 'Guest Feedback', status: hasLocations ? 'Ready' : 'Pending', live: hasLocations },
    ];

    const allLive = statusItems.every(s => s.live);

    return (
      <div className="flex flex-col items-center py-4 px-2 space-y-5" data-testid="wizard-complete">
        {/* Header */}
        <div className="text-center space-y-1.5">
          <div className={`w-14 h-14 rounded-2xl mx-auto flex items-center justify-center ${allLive ? 'bg-green-500/20' : 'bg-amber-500/20'}`}>
            {allLive ? <CheckCircle2 className="w-7 h-7 text-green-400" /> : <AlertCircle className="w-7 h-7 text-amber-400" />}
          </div>
          <h2 className="text-xl font-semibold text-white tracking-tight">
            {allLive ? 'System Live' : 'Almost There'}
          </h2>
          <p className="text-xs text-[hsl(215,20%,55%)] max-w-xs mx-auto">
            {allLive
              ? `${hotel?.name || 'Your property'} is configured and operational.`
              : 'Complete the remaining items to go fully live.'}
          </p>
        </div>

        {/* Configuration Summary */}
        <div className="w-full max-w-sm space-y-1.5">
          <p className="text-[10px] font-medium uppercase tracking-wider text-[hsl(215,20%,45%)] px-1">Configuration</p>
          <div className="grid grid-cols-2 gap-2">
            {configItems.map(({ icon: Icon, label, value, color }) => (
              <div key={label} className="p-2.5 rounded-lg bg-[hsl(217,19%,14%)] border border-[hsl(217,19%,22%)]">
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon className={`w-3.5 h-3.5 ${color}`} />
                  <span className="text-[11px] text-[hsl(215,20%,55%)]">{label}</span>
                </div>
                <span className="text-lg font-semibold text-white">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* System Status */}
        <div className="w-full max-w-sm space-y-1.5">
          <p className="text-[10px] font-medium uppercase tracking-wider text-[hsl(215,20%,45%)] px-1">System Status</p>
          <div className="rounded-lg border border-[hsl(217,19%,22%)] bg-[hsl(217,19%,12%)] divide-y divide-[hsl(217,19%,20%)]">
            {statusItems.map(({ icon: Icon, label, status, live }) => (
              <div key={label} className="flex items-center justify-between px-3 py-2.5" data-testid={`wizard-status-${label.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${live ? 'text-green-400' : 'text-[hsl(215,20%,40%)]'}`} />
                  <span className="text-sm text-[hsl(215,20%,75%)]">{label}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className={`w-1.5 h-1.5 rounded-full ${live ? 'bg-green-400 animate-pulse' : 'bg-[hsl(215,20%,35%)]'}`} />
                  <span className={`text-xs font-medium ${live ? 'text-green-400' : 'text-[hsl(215,20%,45%)]'}`}>{status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-[11px] text-[hsl(215,20%,45%)]">
          All settings can be adjusted from the main portal at any time.
        </p>
      </div>
    );
  };

  const stepRenderers = {
    welcome: renderWelcome,
    locations: renderLocations,
    employees: renderEmployees,
    notifications: renderNotifications,
    branding: renderBranding,
    complete: renderComplete,
  };

  const currentStepId = STEPS[currentStep].id;
  const isLastStep = currentStep === STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-[520px] bg-[hsl(222,47%,11%)] border-[hsl(217,19%,22%)] p-0 gap-0 max-h-[90vh] overflow-hidden flex flex-col" data-testid="setup-wizard-dialog">
        {/* Progress Bar */}
        <div className="px-5 pt-4 pb-2">
          <div className="flex items-center justify-between mb-2">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              const isActive = i === currentStep;
              const isDone = i < currentStep;
              return (
                <div key={step.id} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    isDone ? 'bg-green-500/20 text-green-400' :
                    isActive ? 'bg-blue-500/20 text-blue-400 ring-2 ring-blue-500/30' :
                    'bg-[hsl(217,19%,15%)] text-[hsl(215,20%,40%)]'
                  }`}>
                    {isDone ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`w-8 sm:w-12 h-0.5 mx-1 rounded-full transition-colors ${
                      isDone ? 'bg-green-500/40' : 'bg-[hsl(217,19%,20%)]'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-[10px] text-center text-[hsl(215,20%,50%)]">
            Step {currentStep + 1} of {STEPS.length}: {STEPS[currentStep].title}
          </p>
        </div>

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto px-5 pb-2">
          {stepRenderers[currentStepId]?.()}
        </div>

        {/* Footer Navigation */}
        <div className="px-5 py-3 border-t border-[hsl(217,19%,20%)] flex items-center justify-between bg-[hsl(217,19%,10%)]">
          {isFirstStep ? (
            <Button variant="ghost" size="sm" onClick={onClose} className="text-[hsl(215,20%,55%)]" data-testid="wizard-skip-btn">
              Skip Setup
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={prevStep} className="text-[hsl(215,20%,65%)]" data-testid="wizard-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          )}

          {isLastStep ? (
            <Button onClick={handleComplete} className="bg-green-600 hover:bg-green-700" data-testid="wizard-finish-btn">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Go to Portal
            </Button>
          ) : (
            <Button onClick={nextStep} className="bg-blue-600 hover:bg-blue-700" data-testid="wizard-next-btn">
              {currentStepId === 'welcome' ? 'Get Started' : 'Continue'} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
